/* global wasm_bindgen */
import { AnchorButton, FocusStyleManager, Icon, Intent } from '@blueprintjs/core';
import { PERMISSIONS } from '@moleculeone/auth';
import * as Sentry from '@sentry/browser';
import { push as changeLocation } from 'connected-react-router';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { Route, Switch, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

import Protected from '@f/components/shared/protected/Protected';
import ProtectedRoute from '@f/components/shared/protected/ProtectedRoute';
import { SciFinderAuthProvider, CreditOverviewProvider, ConfigProvider } from '@f/context';
import { disableReportDesigningMode } from '@f/services/report/report.actions';
import { isDesigningModeEnabled } from '@f/services/report/report.selectors';
import { BottomRightToaster } from '@f/services/toasters';
import { signIn } from '@f/services/authentication/authentication.actions';

import { initializeSentry } from '../sentry';

import DebugTools from './debug-tools/DebugTools';
import { Footer } from './footer/Footer';
import Header from './header/Header';
import NotificationToaster from './notification-toaster/NotificationToaster';
import Dashboard from './pages/dashboard/Dashboard';
import SignDocumentsPage from './pages/sign-documents/SignDocumentsPage';
import WelcomePage from './pages/welcome/WelcomePage';
import AppLoader from './shared/app-loader/AppLoader';
import cls from './App.module.css';

FocusStyleManager.onlyShowFocusOnTabs();

class App extends PureComponent {
  static propTypes = {
    isReportDesigningMode: PropTypes.bool,
    designingReportId: PropTypes.string,
    changeLocation: PropTypes.func,
    disableReportDesigningMode: PropTypes.func,
  };

  state = {
    webassemblyLoaded: false,
    webassemblyError: '',
  };

  async componentDidMount() {
    try {
      await wasm_bindgen('/rustchem-wasm-web/rustchem_wasm_bg.wasm');
    } catch (err) {
      this.setState({
        webassemblyError: err.message,
      });
    }
    await this.props.signIn();
    this.setState({
      webassemblyLoaded: true,
    });
  }

  componentDidUpdate(prevProps) {
    const { isReportDesigningMode } = this.props;
    const { isReportDesigningMode: prevIsReportDesigningMode } = prevProps;

    const shouldShowReportToast = isReportDesigningMode && !prevIsReportDesigningMode;
    const shouldDismissReportToast = !isReportDesigningMode && prevIsReportDesigningMode;

    if (shouldShowReportToast) {
      this.showReportToast();
    }

    if (shouldDismissReportToast) {
      this.dismissReportToast();
    }
  }

  componentDidCatch(error, errorInfo) {
    Sentry.withScope(scope => {
      Object.keys(errorInfo).forEach(key => {
        scope.setExtra(key, errorInfo[key]);
      });
      Sentry.captureException(error);
    });
  }

  showReportToast = () => {
    BottomRightToaster.show(
      {
        className: cls.reportToast,
        intent: Intent.PRIMARY,
        icon: 'edit',
        message: (
          <>
            <span>You're currently editing a report</span>
            <AnchorButton minimal text="Show report" icon="document" onClick={this.onShowEditedReport} />
          </>
        ),
        timeout: 0,
        action: {
          text: 'Stop editing',
          icon: 'cross',
          onClick: this.onDisableReportEditingMode,
        },
        isCloseButtonShown: false,
        onDismiss: () => {
          setTimeout(() => {
            const { isReportDesigningMode } = this.props;

            if (isReportDesigningMode) {
              this.showReportToast();
            }
          }, 0);
        },
      },
      'report'
    );
  };

  dismissReportToast = () => {
    BottomRightToaster.dismiss('report');
  };

  onShowEditedReport = () => {
    const { designingReportId } = this.props;
    this.props.changeLocation(`/dashboard/reports/${designingReportId}`);
  };

  onDisableReportEditingMode = () => {
    this.props.disableReportDesigningMode();
  };

  render() {
    const { webassemblyError, webassemblyLoaded } = this.state;

    if (!webassemblyLoaded) {
      return <AppLoader />;
    }

    return (
      <ConfigProvider onConfigReady={config => initializeSentry(config.o11y)}>
        <SciFinderAuthProvider>
          <CreditOverviewProvider>
            <Switch>
              <Route path="/">
                <div className={cls.app} data-test="app-container">
                  {webassemblyError && (
                    <div className={cls.webassemblyError}>
                      <div className={cls.main}>
                        <h2>
                          <Icon icon="error" /> WebAssembly failed to initialize
                        </h2>
                        <p>
                          The application will not work correctly. Please try disabling your ad-blocker or
                          <a href="https://developer.mozilla.org/en-US/docs/WebAssembly#browser_compatibility">
                            check that your browser supports WebAssembly
                          </a>{' '}
                          and does not block it within its settings.
                        </p>
                        <p>
                          Reported error: <code>{webassemblyError}</code>
                        </p>
                      </div>
                    </div>
                  )}
                  <Protected allowedPermissions={[PERMISSIONS.RETRO.DEBUG]}>
                    <DebugTools />
                  </Protected>

                  <Header />

                  <div className={cls.main}>
                    <Switch>
                      <Route exact path="/" component={WelcomePage} />
                      <Route path="/sign-documents" component={SignDocumentsPage} />
                      <ProtectedRoute
                        path="/dashboard"
                        allowedPermissions={[PERMISSIONS.RETRO.APP.READ]}
                        component={Dashboard}
                      />
                    </Switch>
                  </div>

                  <Footer />

                  <NotificationToaster />
                </div>
              </Route>
            </Switch>
          </CreditOverviewProvider>
        </SciFinderAuthProvider>
      </ConfigProvider>
    );
  }
}

export default withRouter(
  connect(
    state => ({
      isReportDesigningMode: isDesigningModeEnabled(state),
      designingReportId: state.report.designingId,
    }),
    { changeLocation, disableReportDesigningMode, signIn }
  )(App)
);
